import React, { Dispatch, ReactNode, SetStateAction } from "react"
import styled from "styled-components"
import Select, { MultiValue } from "react-select"
import CreatableSelect from "react-select/creatable"
import { Common } from "lib/types"

interface StyledProps {
  $show?: boolean
  $invalid?: boolean
}

const Container = styled.div<StyledProps>`
  width: 100%;
  svg {
    margin-right: 0;
  }

  div[class*="-control"] {
    min-height: 4.5rem;
    padding: 0.1rem 0 0 0.8rem;
    border: 0.1rem solid
      ${(p) => (p.$invalid ? p.theme.colors.error.background : p.theme.colors.greyLight)};
    border-radius: 0.5rem;
    transition: all 0.2s;
    cursor: pointer;
    box-shadow: 0 0 0.5rem 0
      ${(p) => (!p.$invalid ? "transparent" : p.theme.colors.error.background)};

    ${(p) => p.theme.mQ.MEDIA_MIN_LARGE} {
      &:hover,
      &:focus {
        border: 0.1rem solid ${(p) => p.theme.colors.turquoiseDark};
      }
    }

    div[class*="-multiValue"] {
      padding: 0 0.5rem 0 0.5rem;
      background: ${(p) => p.theme.colors.greyLighter};
      display: flex;
      align-items: center;
      margin: 0.35rem 0.7rem 0.35rem 0;
      height: 2.8rem;
    }

    div[class*="-ValueContainer"] {
      padding-left: 0;
    }

    div[class*="-MultiValueRemove"] {
      svg {
        fill: ${(p) => p.theme.colors.black};
      }

      &:hover {
        background: none;

        svg {
          fill: ${(p) => p.theme.colors.turquoiseDark};
        }
      }
    }

    div[class*="-placeholder"] {
      margin-left: 1.2rem;
      color: ${(p) => p.theme.colors.greyDarker};
    }
  }
`

const Label = styled.label`
  font-weight: 700;
  display: flex;
  align-items: center;
  padding-bottom: 0.3rem;
`

const ErrorMessage = styled.p<StyledProps>`
  position: relative;
  padding: 0 1.2rem 0 0.5rem;
  font-size: 1.2rem;
  color: ${(p) => p.theme.colors.error.background} !important;
  letter-spacing: 0.02rem;
  transition: opacity 0.2s;
  opacity: ${(p) => (p.$show ? "1" : "0")};
  margin: 0;
`

const allValue = "all"

type Props = {
  // eslint-disable-next-line
  setValue?: Dispatch<SetStateAction<any>>
  options: Common.Option[] | []
  defaultValue?: Common.Option[] | undefined
  placeholder?: string
  onInputChange?: (event: string) => void
  loading?: boolean
  returnValueOnly?: boolean
  invalid?: boolean
  label?: string | ReactNode
  value?: Common.Option[]
  disabled?: boolean
  /**
   * @deprecated Use ProtectedInput wrapper instead of overrideDisable
   * This prop is kept for backward compatibility but will be removed in future versions
   */
  overrideDisable?: boolean
  selectAllEnabled?: boolean
  /** When true, the dropdown becomes a tag/chip input — users can type any
   *  value and press Enter to add it. Use for free-text value lists where
   *  the backing field is a `ComparisonString.values[]`. */
  creatable?: boolean
  /** Optional cap on the number of tags the user can add. */
  maxTags?: number
  /** Optional cap on each tag's character length. */
  maxTagLength?: number
}
// eslint-disable-next-line
const MultiSelect: React.FC<Props> = React.forwardRef<any, Props>(
  (
    {
      options,
      setValue,
      defaultValue,
      placeholder,
      onInputChange,
      loading,
      returnValueOnly,
      invalid = false,
      label,
      disabled,
      value,
      overrideDisable,
      selectAllEnabled = false,
      creatable = false,
      maxTags,
      maxTagLength
    }: Props,
    ref
  ) => {
    const isDisabled = () => {
      if (disabled) return disabled
      if (overrideDisable !== undefined) return !overrideDisable
      return false
    }

    const selectOptions = selectAllEnabled
      ? [{ value: allValue, label: "Select all" }, ...(options || [])]
      : options || []

    const handleOnChange = (selected: MultiValue<Common.Option>) => {
      if (selected[selected.length - 1]?.value === allValue) {
        return setValue && setValue(options || [])
      }
      let next: MultiValue<Common.Option> = selected
      if (creatable && maxTagLength !== undefined) {
        next = next.filter(
          (s) => typeof s.value === "string" && s.value.length <= maxTagLength
        )
      }
      if (creatable && maxTags !== undefined && next.length > maxTags) {
        next = next.slice(0, maxTags)
      }
      if (returnValueOnly) {
        return setValue && setValue(next.map((item) => item.value))
      }
      return setValue && setValue(next)
    }

    const SelectComponent = creatable ? CreatableSelect : Select
    const isAtTagLimit =
      creatable && maxTags !== undefined && (value?.length ?? 0) >= maxTags

    /** Bulk-paste handler for creatable mode: splits the clipboard text on
     *  whitespace / commas / semicolons / newlines, dedupes against the
     *  current value list, applies maxTags / maxTagLength caps, and emits
     *  the merged list. Prevents the default paste so the raw text doesn't
     *  end up in react-select's input. */
    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (!creatable) return
      const text = e.clipboardData.getData("text")
      if (!text) return
      const parsed = text
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
      if (parsed.length === 0) return
      e.preventDefault()
      const existing = value ?? []
      const seen = new Set(existing.map((o) => o.value))
      const merged: Common.Option[] = [...existing]
      for (const raw of parsed) {
        if (seen.has(raw)) continue
        if (maxTagLength !== undefined && raw.length > maxTagLength) continue
        if (maxTags !== undefined && merged.length >= maxTags) break
        merged.push({ value: raw, label: raw })
        seen.add(raw)
      }
      if (merged.length === existing.length) return
      if (returnValueOnly) {
        setValue && setValue(merged.map((o) => o.value))
      } else {
        setValue && setValue(merged)
      }
    }

    return (
      <Container $invalid={invalid} onPaste={handlePaste}>
        {label && (
          <Label>
            {label}
            <ErrorMessage $show={invalid}> - Please select at least one option</ErrorMessage>
          </Label>
        )}
        <SelectComponent
          defaultValue={defaultValue}
          value={value}
          isMulti
          ref={ref}
          onChange={(e) => handleOnChange(e)}
          onInputChange={onInputChange}
          options={selectOptions}
          placeholder={
            placeholder ?? (creatable ? "Type a value and press Enter…" : undefined)
          }
          isDisabled={isDisabled()}
          isLoading={loading || false}
          {...(creatable && {
            formatCreateLabel: (input: string) => `Add "${input}"`,
            noOptionsMessage: () =>
              isAtTagLimit
                ? `Limit of ${maxTags} reached`
                : "Type, press Enter to add — or paste a list"
          })}
        />
      </Container>
    )
  }
)

export default MultiSelect
